import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';
import { RadixButton, RadixCard, RadixInput, RadixSpinner } from '@/components/radix';
import { useLoginV3Flow } from './LoginV3FlowProvider';
import { loginV3IsEightDigitPhoneIdentifier } from './loginV3UserIdentifier';
import { TiWarningOutline } from 'react-icons/ti';

export default function LoginV3FormCard() {
    const { state, setLoginCredentials, submitCredentialLogin, openPasswordReset } = useLoginV3Flow();
    const [showPassword, setShowPassword] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            user: state.login.user,
            password: state.login.password,
        },
        validationSchema: Yup.object({
            user: Yup.string()
                .required(t('Common.EmailRequired'))
                .test('email-or-phone', t('Common.LoginUserInvalid'), (value) => {
                    if (!value || !value.trim()) return false;
                    const v = value.trim();
                    if (loginV3IsEightDigitPhoneIdentifier(v)) return true;
                    return Yup.string().email(t('Common.PleaseEnterValidEmail')).isValidSync(v);
                }),
            password: Yup.string().required(t('Common.LoginResetPasswordRequired')),
        }),
        onSubmit: async (values) => {
            await submitCredentialLogin(null, {
                user: values.user.trim(),
                password: values.password,
            });
        },
    });

    return (
        <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-[0_20px_60px_rgba(0,0,0,0.06)] rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
            {state.emailLoginRequiredAlert ? (
                <div
                    role="alert"
                    className="flex items-center gap-3 border-b border-amber-100 bg-amber-50/50 px-4 py-3 sm:px-6 text-left text-sm text-amber-900 backdrop-blur-sm"
                >
                    <div className="flex items-center gap-2.5 font-medium">
                        <TiWarningOutline size={20} className="text-amber-600 shrink-0" />
                        <span className="leading-tight">{t('Common.LoginMustUseEmailNotPhone')}</span>
                    </div>
                </div>
            ) : null}
            <div className="bg-white p-6 sm:p-8 pb-4 text-center border-b border-[#F0EDE8]">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">{t('Common.LoginMT')}</h1>
            </div>

            <div className="p-5 sm:p-8 pt-6">
                <form className="space-y-4 sm:space-y-6" onSubmit={formik.handleSubmit}>
                    <RadixInput
                        id="login-user"
                        name="user"
                        autoComplete="username"
                        label={t('Common.LoginUserLabel')}
                        type="text"
                        placeholder={t('Common.LoginUserPlaceholder')}
                        value={formik.values.user}
                        onChange={(e) => {
                            void formik.setFieldValue('user', e.target.value);
                            setLoginCredentials({ user: e.target.value });
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.user && formik.errors.user ? formik.errors.user : undefined}
                        className="!rounded-xl !bg-white !h-12 sm:!h-14 !border-[#E6E1D9] focus:!border-primary-500"
                    />

                    <div className="space-y-1">
                        <RadixInput
                            id="login-password"
                            name="password"
                            autoComplete="current-password"
                            label={t('Common.LoginPass')}
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('Common.LoginPass')}
                            value={formik.values.password}
                            onChange={(e) => {
                                void formik.setFieldValue('password', e.target.value);
                                setLoginCredentials({ password: e.target.value });
                            }}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.password && formik.errors.password ? formik.errors.password : undefined
                            }
                            className="!rounded-xl !bg-white !h-12 sm:!h-14 !border-[#E6E1D9] focus:!border-primary-500"
                            endComponent={
                                <span className="pointer-events-auto">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="pr-3 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                                    >
                                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                    </button>
                                </span>
                            }
                        />
                        <div className="flex justify-end pr-1">
                            <span className="pointer-events-auto">
                                <button
                                    type="button"
                                    onClick={openPasswordReset}
                                    className="text-[11px] sm:text-xs font-semibold text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                                >
                                    {t('Common.LogForPass')}?
                                </button>
                            </span>
                        </div>
                    </div>

                    <RadixButton
                        type="submit"
                        variant="primary"
                        className="w-full !h-12 sm:!h-14 !rounded-xl !text-base font-bold shadow-lg shadow-primary-500/10 mt-2 text-white"
                        disabled={state.isLoading}
                    >
                        {state.isLoading ? <RadixSpinner variant="white" size="sm" /> : t('Common.LogBtnT')}
                    </RadixButton>
                </form>
            </div>
        </RadixCard>
    );
}

const EyeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
);
